import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { ArrowRight, Users, Compass, Globe } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import heroImg from "@/assets/pulso33-hero.jpg";

export const Route = createFileRoute("/about/")({
  component: AboutIndex,
  head: () => ({
    meta: [
      { title: "About — PULSO33" },
      { name: "description", content: "Meet PULSO33: a remote-first engineering team shipping AI agents, web apps, cloud infrastructure and BI for companies that want to move." },
      { property: "og:title", content: "About PULSO33" },
      { property: "og:description", content: "Team, mission and where we work." },
      { property: "og:url", content: "https://pulso33.com/about" },
      { property: "og:image", content: heroImg },
    ],
    links: [{ rel: "canonical", href: "https://pulso33.com/about" }],
  }),
});

function AboutIndex() {
  const { tk } = useT();
  const hero = useMedia("about.hero", heroImg, "PULSO33 team at work");

  const cards = [
    {
      to: "/about/equipo" as const,
      icon: Users,
      kicker: tk("about.index.team.kicker", "Team"),
      title: tk("about.index.team.title", "Meet the engineers"),
      desc: tk("about.index.team.desc", "Senior engineers across AI, cloud, web and data. The people who actually build and run your systems."),
    },
    {
      to: "/about/mision" as const,
      icon: Compass,
      kicker: tk("about.index.mision.kicker", "Mission"),
      title: tk("about.index.mision.title", "How we work"),
      desc: tk("about.index.mision.desc", "Pragmatic, transparent and long-term. We ship production systems and stand behind them."),
    },
    {
      to: "/about/oficinas" as const,
      icon: Globe,
      kicker: tk("about.index.oficinas.kicker", "Where"),
      title: tk("about.index.oficinas.title", "Remote-first, worldwide"),
      desc: tk("about.index.oficinas.desc", "A distributed team across Europe and the Americas. On your hours when it matters."),
    },
  ];

  return (
    <Layout>
      <section className="relative border-b border-gold/15">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${hero.url})` }} aria-hidden />
        <div className="absolute inset-0 bg-background/75 backdrop-blur-[2px]" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("about.index.kicker", "About PULSO33")}
          </span>
          <h1 className="font-serif text-5xl md:text-7xl mt-5 max-w-4xl mx-auto">
            {tk("about.index.title.a", "Engineering, ")}
            <em className="not-italic gold-gradient">{tk("about.index.title.b", "delivered.")}</em>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {tk("about.index.intro", "We design, build and run the IT systems that matter most to your business.")}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                className="group rounded-2xl border border-gold/20 bg-card/30 p-8 flex flex-col hover:border-gold/60 hover:bg-card/60 transition-colors"
              >
                <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-gold/80">
                  <Icon size={16} className="text-gold" />
                  <span>{c.kicker}</span>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl mt-5 group-hover:text-gold transition-colors">{c.title}</h2>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed flex-1">{c.desc}</p>
                <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-gold/80 group-hover:text-gold">
                  {tk("about.index.cta", "Learn more")} <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </Layout>
  );
}
